import React from "react";

import EntityLink from "@/components/Changelog/ChangelogItem/EventHeader/EntityLink";
import { RegistryItem } from "@/components/UnifiedRegistry/types";
import { getItemLink } from "@/components/UnifiedRegistry/utils";
import { EntityIcon } from "@/components/ui/icons";
import { useUser } from "@/context/user-context";
import { EntityType } from "@/queries/folder-entities/types";
import { TagMentionAttrs } from "@/components/ContentArea/Nodes/Tag/types";
import {
  TAG_MENTION_USER_MAX_WIDTH_CLASS,
  tagMentionEntityWrapperClassName,
} from "@/components/ContentArea/Nodes/Tag/tagMentionStyles";
import {
  isTagLogEntityType,
  TagLogEntityLink,
} from "@/components/ContentArea/Nodes/Tag/TagLogEntityLink";

const mapEntityTypeToEnum = (entityType: string | undefined): EntityType => {
  switch (entityType) {
    case "PROMPT":
      return EntityType.PROMPT;
    case "WORKFLOW":
      return EntityType.WORKFLOW;
    case "DATASET":
      return EntityType.DATASET;
    case "REPORT":
      return EntityType.REPORT;
    case "AB_TEST":
      return EntityType.AB_TEST;
    case "FOLDER":
      return EntityType.FOLDER;
    case "SNIPPET":
      return EntityType.SNIPPET;
    case "INPUT_VARIABLE_SET":
      return EntityType.INPUT_VARIABLE_SET;
    case "SKILL_COLLECTION":
      return EntityType.SKILL_COLLECTION;
    case "SMART_TABLE":
      return EntityType.SMART_TABLE;
    case "TOOL":
      return EntityType.TOOL;
    default:
      return EntityType.PROMPT;
  }
};

const getTagEntityLink = ({
  entityType,
  id,
  name,
  link,
  workspaceId,
}: {
  entityType: EntityType;
  id?: number | string;
  name: string;
  link?: string;
  workspaceId?: number | null;
}) => {
  if (link) {
    return link;
  }

  if (!workspaceId || id === undefined) {
    return undefined;
  }

  if (entityType === EntityType.SKILL_COLLECTION) {
    return `/workspace/${workspaceId}/skill-collection/${id}`;
  }

  if (entityType === EntityType.SMART_TABLE) {
    return `/workspace/${workspaceId}/smart-tables/${id}`;
  }

  return getItemLink(
    {
      id: Number(id),
      type: entityType,
      name,
    } as RegistryItem,
    workspaceId,
  );
};

export const TagMentionRenderer = ({ tag }: { tag: TagMentionAttrs }) => {
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;

  if (tag.tagType === "user") {
    return (
      <span
        title={tag.name}
        className={`tag-mention tag-mention-user inline truncate rounded bg-blue-50 px-1 py-0.5 text-xs font-medium text-blue-600 ${TAG_MENTION_USER_MAX_WIDTH_CLASS}`}
      >
        @{tag.name}
      </span>
    );
  }

  if (tag.tagType === "entityType") {
    const entityType = mapEntityTypeToEnum(tag.entityType);
    const displayName = tag.name.charAt(0).toUpperCase() + tag.name.slice(1);
    return (
      <span className="tag-mention tag-mention-entity-type inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 align-middle text-xs font-medium text-gray-900">
        <EntityIcon type={entityType} size="xs" className="flex-shrink-0" />
        <span className="text-gray-900">{displayName}</span>
      </span>
    );
  }

  // Entity tag
  if (tag.tagType === "entity" && tag.entityType && (tag.id !== undefined || tag.link)) {
    if (isTagLogEntityType(tag.entityType)) {
      return (
        <span className={tagMentionEntityWrapperClassName()}>
          <TagLogEntityLink
            entityType={tag.entityType}
            name={tag.name}
            link={tag.link}
            size="xs"
            className="min-w-0 max-w-full"
          />
        </span>
      );
    }

    const entityType = mapEntityTypeToEnum(tag.entityType);
    const entityLink = getTagEntityLink({
      entityType,
      id: tag.id,
      name: tag.name,
      link: tag.link,
      workspaceId,
    });

    return (
      <span className={tagMentionEntityWrapperClassName()}>
        <EntityLink
          type={entityType}
          name={tag.name}
          link={entityLink}
          size="xs"
          className="min-w-0 max-w-full"
        />
      </span>
    );
  }

  return null;
};
